import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";

interface Enable2FADialogProps {
  open: boolean;
  onClose: () => void;
  onEnable: () => void;
}

const STEPS = ["Set Up Authenticator", "Verify Code", "Save Backup Codes"];

const BACKUP_CODES = [
  "ABCD-1234",
  "EFGH-5678",
  "IJKL-9012",
  "MNOP-3456",
  "QRST-7890",
  "UVWX-1234",
  "YZAB-5678",
  "CDEF-9012",
];

const Enable2FADialog: React.FC<Enable2FADialogProps> = ({ open, onClose, onEnable }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");

  const handleClose = () => {
    setActiveStep(0);
    setVerificationCode("");
    onClose();
  };

  const handleNext = () => {
    if (activeStep === STEPS.length - 1) {
      onEnable();
      handleClose();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enable Two-Factor Authentication</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 160,
                height: 160,
                backgroundColor: "#F5F5F5",
                border: "2px dashed #E0E0E0",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <QrCode2Icon sx={{ fontSize: 72, color: "#178B8E" }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                QR Code
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Or enter this key manually:{" "}
              <Box
                component="span"
                sx={{ fontFamily: "monospace", fontWeight: 700, color: "#178B8E" }}
              >
                ABCD EFGH IJKL MNOP
              </Box>
            </Typography>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter the 6-digit code from your authenticator app to verify setup.
            </Typography>
            <TextField
              label="Verification Code"
              value={verificationCode}
              onChange={(e) =>
                setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              fullWidth
              size="small"
              placeholder="000000"
              inputProps={{ maxLength: 6 }}
            />
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Save these backup codes in a safe place. Each code can only be used once if you
              lose access to your authenticator app.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                p: 2,
                backgroundColor: "#F5F5F5",
                borderRadius: 1,
                mb: 2,
              }}
            >
              {BACKUP_CODES.map((code) => (
                <Typography
                  key={code}
                  variant="body2"
                  sx={{ fontFamily: "monospace", fontWeight: 500 }}
                >
                  {code}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: "none", borderColor: "#178B8E", color: "#178B8E" }}
              >
                Copy All
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{ textTransform: "none", borderColor: "#178B8E", color: "#178B8E" }}
              >
                Download
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={activeStep === 1 && verificationCode.length !== 6}
          sx={{ textTransform: "none", backgroundColor: "#178B8E" }}
        >
          {activeStep === STEPS.length - 1 ? "Finish Setup" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Enable2FADialog;
